import { isEmpty, omit } from "lodash-es";
import { useEffect } from "react";
import type { UseAsyncStateCallback } from "../../../utils/hooks/use-async-state-callback";
import { useAsyncStateCallback } from "../../../utils/hooks/use-async-state-callback";
import type { EmailDesign } from "../../email-design/server/supabase-email-design-service";
import { makeEmptySerializedState, makeHeaderSerializedState } from "../../email-design/util";
import { makeDefaultLexicalDesign } from "../../lexical-chat-moment/make-default-lexical-design";
import type { SerialisedEditorDesign } from "../../message/editor/types/serialised-editor-design";

export type ConstructedDesign = {
  design: SerialisedEditorDesign;
  header: SerialisedEditorDesign | null;
  footer: SerialisedEditorDesign | null;
  settings: {
    text: EmailDesign["text_settings"];
    layout: EmailDesign["layout_settings"];
    footer_layout: EmailDesign["footer_layout_settings"];
    buttons: EmailDesign["button_settings"];
    dividers: EmailDesign["divider_settings"];
  };
  headerSettings: EmailDesign["header"];
};

export function useConstructedDesign(
  design: SerialisedEditorDesign | null,
  selectedEmailDesign: EmailDesign | null,
): {
  constructDesign: Omit<UseAsyncStateCallback<ConstructedDesign, [EmailDesign]>, "load">;
} {
  const baseDesign = isEmpty(design) ? makeDefaultLexicalDesign("email") : design;

  const constructDesign = useAsyncStateCallback(
    async (emailDesign: EmailDesign) => {
      return {
        design:
          baseDesign && !isEmpty(baseDesign)
            ? baseDesign
            : makeEmptySerializedState([
                {
                  type: "paragraph",
                  format: "left",
                  indent: 0,
                  version: 1,
                  children: [],
                  direction: null,
                },
              ]),
        header: await makeHeaderSerializedState(emailDesign.header),
        headerSettings: emailDesign.header,
        footer: emailDesign.footer,
        settings: {
          text: emailDesign.text_settings,
          layout: emailDesign.layout_settings,
          footer_layout: emailDesign.footer_layout_settings,
          buttons: emailDesign.button_settings,
          dividers: emailDesign.divider_settings,
        },
      };
    },
    [baseDesign],
  );

  const { load } = constructDesign;

  useEffect(() => {
    if (selectedEmailDesign) {
      void load(selectedEmailDesign).catch(console.error);
    }
  }, [load, selectedEmailDesign]);

  return {
    constructDesign: omit(constructDesign, "load"),
  };
}
